import { useState, useCallback } from 'react'
import {
  BleClient,
  dataViewToNumbers,
  numbersToDataView,
  textToDataView,
  numberToUUID,
} from '@capacitor-community/bluetooth-le'
import { noop } from '@iqueue/ui-kit'
import { Parser } from 'binary-parser'

// const BLE_SCAN_TIMEOUT = 2 * 60 * 1000

/**
 * Формат команды
 *
 * -> II XX XX XX ...
 * <- II YY YY YY
 *
 * Где
 * II — ID команды
 * XX — данные запроса
 * YY — данные ответа
 *
 * Команды:
 *
 * Get Device Info
 * -> 35
 * <- 35 STATUS(1B > 0)
 * <- 35 UNIT(1B) MIN_SENSOR_VALUE(4B be) MAX_SENSOR_VALUE(4B be) MIN_WORK_VALUE(4B be) MAX_WORK_VALUE(4B be)
 *
 * Get Uplinks Count
 * -> 38
 * <- 38 COUNT (4B be)
 *
 * Set Sensor Unit And Range
 * -> 55 UNIT(1B) MIN_WORK_VALUE(4B be) MAX_WORK_VALUE(4B be)
 * <- 55 STATUS (0 — OK, 1 — unknown error, 2 — invalid unit, 3 — invalid range)
 *
 * Set cold start time
 * -> 56 VALUE(2B be seconds)
 * <- 56 STATUS (0 — OK, 1 unknown error)
 *
 * Set duty cycle
 * -> 57 VALUE(2B be seconds)
 * <- 57 STATUS (0 — OK, 1 unknown error)
 *
 * Read Current Sensor Value
 * -> 01
 * <- 01 BATTERY(2B be) DEVICE_TEMPERATURE(1B be) VALUE(4B)
 *
 * Броадкасты:
 *
 * <- DEV_EUI
 */

const errorParser = new Parser()
  .uint8('id')
  .uint8('errorCode')

const dataParser = new Parser()
  .uint8('id')
  .choice(null, {
    tag: 'id',
    choices: {
      0x01: Parser.start()
        .uint16le('battery')
        .uint8('deviceTemperature')
        .floatbe('value'),
      0x02: Parser.start()
        .uint16be('battery')
        .uint8('deviceTemperature')
        .floatbe('value')
        .uint16be('dutyCycle')
        .uint16be('coldStart')
        .uint8('quantity')
        .uint16be('samplesPeriod')
        .uint8('unit')
        .floatbe('minWorkValue')
        .floatbe('maxWorkValue'),
      0x35: Parser.start()
        .uint8('unit')
        .floatle('minSensorValue')
        .floatle('maxSensorValue')
        .floatle('minWorkValue')
        .floatle('maxWorkValue'),
      0x36: Parser.start()
        .uint16le('coldStart'),
      0x37: Parser.start()
        .uint16le('dutyCycle'),
      0x38: Parser.start()
        .uint32le('count'),
      0x55: Parser.start()
        .uint8('unit')
        .floatle('minWorkValue')
        .floatle('maxWorkValue'),
      0x56: Parser.start()
        .uint16le('coldStart'),
      0x57: Parser.start()
        .uint16le('dutyCycle'),
    },
  })

function bleParse(buffer) {
  const data = new Uint8Array(buffer)

  return (data.length === 2 ? errorParser : dataParser).parse(data)
}

console.log('VA1', bleParse([
  0x02,
  0x00,
  0x00,
  0x18,
  0x3c,
  0x04,
  0x59,
  0x7c,
  0x01,
  0x01,
  0x1b,
  0x58,
  0x01,
  0x00,
  0x01,
  0x42,
  0x00,
  0x00,
  0x00,
  0x3f,
  0xc8,
  0x00,
  0x00,
  0x80,
]))
console.log('VA2', bleParse([
  0x02,
  0x03,
  0x85,
  0x18,
  0x3c,
  0x40,
  0x95,
  0x6d,
  0x00,
  0x1e,
  0x09,
  0xc4,
  0x0a,
  0x00,
  0x0a,
  0x0a,
  0x80,
  0x00,
  0x00,
  0x00,
  0x42,
  0xc8,
  0x00,
  0x00,
]))

// console.log('0x01', bleParse([
//   0x01, 0x0E, 0x10, 0x20,
//   0x3C, 0x81, 0x94, 0xC6,
// ]))
//
// console.log('0x35', bleParse([
//   0x35, 0x0A, 0xBF, 0x82,
//   0x86, 0x10, 0x42, 0xCB,
//   0xF1, 0x79, 0x00, 0x00,
//   0x00, 0x3F, 0x20, 0x00,
//   0x00, 0x00,
// ]))
//
// console.log('0x36', bleParse([
//   0x36, 0x1B, 0x58,
// ]))
//
// console.log('0x37', bleParse([
//   0x37, 0x00, 0x1E,
// ]))
//
// console.log('0x38', bleParse([
//   0x38, 0x00, 0x00, 0x00, 0x31
// ]))
//
// console.log('ERROR', bleParse([0x35, 0x01]))

function safeUUID(value) {
  if (typeof value === 'number') return numberToUUID(value)
  return value
}

const DEFAULT_DEVICE = {
  connected: false,
  id: '',
  sn: '',
  name: '',
  model: '',
  hwVersion: '',
  fwVersion: '',
}

export function useBLE() {
  const [device, setDevice] = useState(DEFAULT_DEVICE)
  const [error, setError] = useState(false)

  const init = useCallback(async () => {
    try {
      await BleClient.initialize()

      console.log('✅ BLE initialized')
    } catch (e) {
      setError(e)
    }
  }, [])

  /**
   * @type {(function(*, *, *): Promise<string>)|*}
   */
  const readString = useCallback(async (deviceId, serviceUuid, charUuid) => {
    try {
      const data = await BleClient.read(deviceId, safeUUID(serviceUuid), safeUUID(charUuid), {
        timeout: 25000
      })

      return data?.buffer ? new TextDecoder().decode(data.buffer) || '' : ''
    } catch (e) {
      console.warn(deviceId, e)
      return ''
    }
  }, [])

  /**
   * @type {(function(*, *, *): Promise<number[]>)|*}
   */
  const readNumbers = useCallback(async (deviceId, serviceUuid, charUuid) => {
    try {
      const data = await BleClient.read(deviceId, safeUUID(serviceUuid), safeUUID(charUuid), {
        timeout: 500
      })

      return data ? dataViewToNumbers(data) : []
    } catch (e) {
      console.warn(deviceId, e)
      return []
    }
  }, [])

  // const scan = useCallback((serviceUuids, {
  //   optionalServices = undefined,
  //   prefix = undefined,
  // }) => {
  //   async function doIt() {
  //     const device = await navigator.bluetooth.requestDevice({
  //       acceptAllDevices: true,
  //       optionalServices: ['0000180a-0000-1000-8000-00805f9b34fb', '00008018-0000-1000-8000-00805f9b34fb'],
  //     })
  //
  //     setDevice({ id: device.id, device: device })
  //
  //     // if (isNative) {
  //     //   return new Promise((resolve, reject) => {
  //     //     const timeout = setTimeout(() => {
  //     //       BleClient.stopLEScan()
  //     //       reject('timeout')
  //     //     }, BLE_SCAN_TIMEOUT)
  //     //
  //     //     BleClient.requestLEScan({
  //     //       filters: prefix ? [{
  //     //         namePrefix: prefix,
  //     //         optionalServices: optionalServices ? optionalServices.map(safeUUID) : undefined
  //     //       }] : undefined,
  //     //       services: serviceUuids ? serviceUuids.map(safeUUID) : [],
  //     //       allowDuplicates: false,
  //     //     }, (result) => {
  //     //       clearTimeout(timeout)
  //     //       BleClient.stopLEScan()
  //     //       const foundDevice = { id: result.device.deviceId }
  //     //       setDevice(foundDevice)
  //     //       resolve(foundDevice)
  //     //     })
  //     //   })
  //     // } else {
  //     //   const btDevice = await navigator.bluetooth.requestDevice({
  //     //     filters: prefix ? [{
  //     //       namePrefix: prefix,
  //     //     }] : undefined,
  //     //     // acceptAllDevices: true,
  //     //     optionalServices: [
  //     //       ...serviceUuids ? serviceUuids.map(safeUUID) : [],
  //     //       ...optionalServices ? optionalServices.map(safeUUID) : [],
  //     //     ],
  //     //   })
  //     //
  //     //   console.log('REQ', {
  //     //     filters: prefix ? [{
  //     //       namePrefix: prefix,
  //     //     }] : undefined,
  //     //     // acceptAllDevices: true,
  //     //     optionalServices: [
  //     //       ...serviceUuids ? serviceUuids.map(safeUUID) : [],
  //     //       ...optionalServices ? optionalServices.map(safeUUID) : [],
  //     //     ],
  //     //   })
  //     //
  //     //   const foundDevice = { id: btDevice.id, device: btDevice }
  //     //   setDevice(foundDevice)
  //     //   return foundDevice
  //     // }
  //   }
  //
  //   doIt().catch(setError)
  // }, [isNative])

  const connect = useCallback(async ({
    services = [],
    optionalServices = [],
    namePrefix,
  }) => {
    try {
      const device = await BleClient.requestDevice({
        namePrefix,
        services: services.map(safeUUID),
        optionalServices: optionalServices.map(safeUUID),
      })

      await BleClient.connect(device.deviceId, () => {
        console.log('Device disconnected')
        setDevice(DEFAULT_DEVICE)
      })

      setError(null)
      setDevice({
        connected: true,
        id: device.deviceId,
        sn: await readString(device.deviceId, 0x180A, 0x2A28),
        name: await readString(device.deviceId, 0x180A, 0x2A29),
        model: await readString(device.deviceId, 0x180A, 0x2A23),
        hwVersion: await readString(device.deviceId, 0x180A, 0x2A27),
        fwVersion: await readString(device.deviceId, 0x180A, 0x2A26),
      })

      console.log('Device connected!')
    } catch (e) {
      setError(e)
      setDevice(DEFAULT_DEVICE)
    }
  }, [readString])

  const disconnect = useCallback(async () => {
    if (!device.connected) return

    try {
      await BleClient.disconnect(device.id)
      setDevice(DEFAULT_DEVICE)
    } catch (e) {
      setError(e)
    }
  }, [device])

  /**
   * @type {(function(*, *, string|number[]): Promise<void>)|*}
   */
  const write = useCallback(async (serviceUuid, charUuid, data) => {
    if (!device.connected) return

    try {
      const dv = typeof data === 'string' ? textToDataView(data) : numbersToDataView(data)
      console.log('WRITE:', safeUUID(serviceUuid), safeUUID(charUuid), dv)
      await BleClient.write(device.id, safeUUID(serviceUuid), safeUUID(charUuid), dv)
    } catch (e) {
      setError(e)
    }
  }, [device])

  // /**
  //  * @type {(function(*, *, asString: boolean): Promise<number[]>)|*}
  //  */
  const read = useCallback(async (serviceUuid, charUuid, asString = false) => {
    if (!device.connected) return asString ? '' : []
    return await (asString ? readString : readNumbers)(device.id, serviceUuid, charUuid)
  }, [device, readString, readNumbers])

  const subscribe = useCallback((serviceUuid, charUuid, callback) => {
    if (!device.connected) return noop
    serviceUuid = safeUUID(serviceUuid)
    charUuid = safeUUID(charUuid)

    BleClient
      .startNotifications(device.id, serviceUuid, charUuid, (data) => {
        try {
          callback(bleParse(data.buffer))
        } catch (ignored) {
          callback(data)
        }
      })
      .catch(setError)

    return function() {
      BleClient
        .stopNotifications(device.id, serviceUuid, charUuid)
        .catch(setError)
    }
  }, [device])

  return { init, error, connect, disconnect, write, read, subscribe, device }
}
